import type { FunctionalComponent } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { useExternalScript } from '@/hooks/use-external-script.ts';

export interface YouTubeCallbacks {
  onReady?: (player: YT.Player) => void;
  onStateChange?: (state: YT.PlayerState, player: YT.Player) => void;
  onError?: (code: number) => void;
}

interface Props {
  videoId?: string;
  callbacks?: YouTubeCallbacks;
}

/**
 * 유튜브 IFrame 플레이어.
 *
 * 조회수는 공식 IFrame Player 로 재생하면 유튜브 조회수에 반영된다. 다만
 * 자동재생 + 음소거는 유튜브가 걸러내므로 `autoplay: 0, mute: 0` 으로 두고
 * 사용자가 직접 재생을 시작하게 한다.
 *
 * 구 프로젝트 대비 달라진 점:
 * - 스크립트 로딩을 `useExternalScript` 로 통일했다. 예전에는 전역
 *   `window.onYouTubeIframeAPIReady` 를 컴포넌트가 덮어써서, 플레이어가 둘 이상
 *   있으면 나중 것만 초기화됐다.
 * - 콜백을 ref 로 들고 가서 videoId 가 그대로인데 플레이어가 다시 만들어지는 것을 막는다.
 * - 재생을 막는 투명 오버레이를 두지 않는다. 사용자가 조작할 수 없게 막고
 *   일시정지를 강제 해제하는 동작은 비정상 재생으로 걸러질 수 있다.
 */
export const YouTubePlayer: FunctionalComponent<Props> = ({ videoId, callbacks }) => {
  const status = useExternalScript('https://www.youtube.com/iframe_api');
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YT.Player | null>(null);

  const handlers = useRef(callbacks);
  handlers.current = callbacks;

  useEffect(() => {
    if (!videoId || status !== 'ready') return;

    // IFrame API 는 스크립트 로드 직후 한 박자 뒤에 window.YT.Player 가 채워진다.
    let cancelled = false;
    let timer = 0;

    const create = () => {
      if (cancelled || !containerRef.current) return;

      if (!window.YT?.Player) {
        timer = window.setTimeout(create, 50);
        return;
      }

      // 같은 플레이어가 살아 있으면 영상만 교체한다.
      if (playerRef.current?.loadVideoById) {
        playerRef.current.loadVideoById(videoId);
        return;
      }

      playerRef.current = new window.YT.Player(containerRef.current, {
        videoId,
        width: '100%',
        height: '100%',
        playerVars: {
          autoplay: 0,
          mute: 0,
          playsinline: 1,
          rel: 0,
          modestbranding: 1,
          iv_load_policy: 3,
          origin: window.location.origin,
        },
        events: {
          onReady: (e) => handlers.current?.onReady?.(e.target),
          onStateChange: (e) => handlers.current?.onStateChange?.(e.data, e.target),
          onError: (e) => handlers.current?.onError?.(e.data),
        },
      });
    };

    create();

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      playerRef.current?.destroy?.();
      playerRef.current = null;
    };
  }, [videoId, status]);

  return <div ref={containerRef} class="youtube-player" />;
};
